import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, Loader2, Search } from "lucide-react";
import { fetchMarcas, fetchModelos, fetchAnos, fetchValor, parseFipeValue, type FipeMarca, type FipeModelo, type FipeAno } from "@/lib/fipe";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface Props {
  leiloeiro: string;
  onNext: (data: {
    marcaId: string; marcaNome: string;
    modeloId: string; modeloNome: string;
    anoCode: string; anoNome: string;
    km: number; observacoes: string;
    valorFipe: number; valorFipeFormatado: string;
  }) => void;
  onBack: () => void;
}

function SearchableSelect({
  label,
  placeholder,
  items,
  value,
  onSelect,
  disabled,
  loading,
  displayKey = "nome",
  valueKey,
}: {
  label: string;
  placeholder: string;
  items: any[];
  value: string;
  onSelect: (val: string) => void;
  disabled?: boolean;
  loading?: boolean;
  displayKey?: string;
  valueKey: string;
}) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter((i) => i[displayKey].toLowerCase().includes(q));
  }, [items, search, displayKey]);

  const selectedItem = items.find((i) => String(i[valueKey]) === value);

  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="relative">
        <button
          type="button"
          disabled={disabled || loading}
          onClick={() => setOpen(!open)}
          className={cn(
            "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            "disabled:cursor-not-allowed disabled:opacity-50",
            "text-left items-center justify-between"
          )}
        >
          <span className={selectedItem ? "text-foreground" : "text-muted-foreground"}>
            {loading ? "Carregando..." : selectedItem ? selectedItem[displayKey] : placeholder}
          </span>
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          ) : (
            <Search className="w-4 h-4 text-muted-foreground" />
          )}
        </button>

        {open && !disabled && !loading && (
          <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-lg">
            <div className="p-2 border-b">
              <Input
                placeholder="Buscar..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
                className="h-8 text-sm"
              />
            </div>
            <div className="max-h-48 overflow-y-auto p-1">
              {filtered.length === 0 ? (
                <p className="text-sm text-muted-foreground p-2 text-center">Nenhum resultado</p>
              ) : (
                filtered.map((item) => (
                  <button
                    key={item[valueKey]}
                    type="button"
                    onClick={() => {
                      onSelect(String(item[valueKey]));
                      setOpen(false);
                      setSearch("");
                    }}
                    className={cn(
                      "w-full text-left px-3 py-1.5 text-sm rounded hover:bg-accent",
                      String(item[valueKey]) === value && "bg-accent font-medium"
                    )}
                  >
                    {item[displayKey]}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function StepDadosVeiculo({ leiloeiro, onNext, onBack }: Props) {
  const [marcas, setMarcas] = useState<FipeMarca[]>([]);
  const [modelos, setModelos] = useState<FipeModelo[]>([]);
  const [anos, setAnos] = useState<FipeAno[]>([]);
  const [loadingMarcas, setLoadingMarcas] = useState(true);
  const [loadingModelos, setLoadingModelos] = useState(false);
  const [loadingAnos, setLoadingAnos] = useState(false);
  const [consultingFipe, setConsultingFipe] = useState(false);

  const [marcaId, setMarcaId] = useState("");
  const [modeloId, setModeloId] = useState("");
  const [anoCode, setAnoCode] = useState("");
  const [km, setKm] = useState("");
  const [obs, setObs] = useState("");

  useEffect(() => {
    fetchMarcas().then(setMarcas).finally(() => setLoadingMarcas(false));
  }, []);

  useEffect(() => {
    if (!marcaId) { setModelos([]); return; }
    setLoadingModelos(true);
    setModeloId(""); setAnoCode("");
    fetchModelos(marcaId).then((d) => setModelos(d.modelos)).finally(() => setLoadingModelos(false));
  }, [marcaId]);

  useEffect(() => {
    if (!marcaId || !modeloId) { setAnos([]); return; }
    setLoadingAnos(true);
    setAnoCode("");
    fetchAnos(marcaId, modeloId).then(setAnos).finally(() => setLoadingAnos(false));
  }, [marcaId, modeloId]);

  const marcaNome = marcas.find((m) => m.codigo === marcaId)?.nome || "";
  const modeloNome = modelos.find((m) => String(m.codigo) === modeloId)?.nome || "";
  const anoNome = anos.find((a) => a.codigo === anoCode)?.nome || "";
  const kmNum = parseInt(km.replace(/\D/g, ""), 10);
  const valid = marcaId && modeloId && anoCode;

  const handleAdvance = async () => {
    if (!valid) return;
    setConsultingFipe(true);
    try {
      const fipeData = await fetchValor(marcaId, modeloId, anoCode);
      const valorFipe = parseFipeValue(fipeData.Valor);
      onNext({
        marcaId, marcaNome, modeloId, modeloNome, anoCode, anoNome,
        km: kmNum || 0, observacoes: obs,
        valorFipe,
        valorFipeFormatado: fipeData.Valor,
      });
    } catch {
      alert("Erro ao consultar tabela FIPE. Verifique os dados e tente novamente.");
    } finally {
      setConsultingFipe(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div className="space-y-2">
        <h2 className="text-3xl font-heading font-bold tracking-tight">Dados do Veículo</h2>
        <p className="text-muted-foreground text-lg">Preencha as informações para a IA cruzar com a tabela FIPE.</p>
      </div>

      <div className="space-y-4">
        <SearchableSelect
          label="Marca"
          placeholder="Selecione a marca"
          items={marcas}
          value={marcaId}
          onSelect={setMarcaId}
          disabled={loadingMarcas}
          loading={loadingMarcas}
          valueKey="codigo"
        />

        <SearchableSelect
          label="Modelo"
          placeholder="Selecione o modelo"
          items={modelos}
          value={modeloId}
          onSelect={setModeloId}
          disabled={!marcaId || loadingModelos}
          loading={loadingModelos}
          valueKey="codigo"
        />

        <SearchableSelect
          label="Ano"
          placeholder="Selecione o ano"
          items={anos}
          value={anoCode}
          onSelect={setAnoCode}
          disabled={!modeloId || loadingAnos}
          loading={loadingAnos}
          valueKey="codigo"
        />

        <div className="space-y-1.5">
          <Label>Quilometragem (opcional)</Label>
          <Input
            type="text"
            inputMode="numeric"
            placeholder="Ex: 45.000 km"
            value={km ? `${Number(km.replace(/\D/g, "")).toLocaleString("pt-BR")} km` : ""}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "");
              if (val.length <= 7) { // Limite razoável para KM
                setKm(val);
              }
            }}
          />
        </div>

        <div className="space-y-1.5">
          <Label>Observações (opcional)</Label>
          <Textarea
            placeholder="Informações adicionais sobre o veículo..."
            value={obs}
            onChange={(e) => setObs(e.target.value)}
            maxLength={500}
          />
        </div>
      </div>

      <div className="flex gap-4 pt-4">
        <Button variant="outline" size="lg" onClick={onBack} className="flex-1">Voltar</Button>
        <Button
          disabled={!valid || consultingFipe}
          onClick={handleAdvance}
          size="lg"
          className="flex-1 group"
        >
          {consultingFipe ? (
            <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Extraindo Dados...</>
          ) : (
            <>
              Avançar
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </>
          )}
        </Button>
      </div>
    </motion.div>
  );
}
